import { useState, useEffect, useCallback, useRef } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Tabs from '@radix-ui/react-tabs'
import * as Switch from '@radix-ui/react-switch'
import { X, Settings, FolderOpen, RefreshCw, Trash2, HardDrive, Loader2, User, LogOut, Monitor, Moon, Save, Clock, Download, Upload, Archive, Shuffle, Calendar } from 'lucide-react'
import { useConnection } from '@/features/connection/ConnectionContext'
import { useSerialCommands, ESPSetup, DeviceTime } from '@/features/connection/useSerialCommands'
import { useGoogleAuth } from '@/features/google-photos/GoogleAuthContext'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { PhotoIndex } from '@/types/photos'

interface SettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const { isSignedIn } = useGoogleAuth()

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-2xl max-h-[85vh] bg-zinc-900 rounded-lg shadow-xl border border-zinc-800 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <Dialog.Title className="text-lg font-semibold text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Settings
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-zinc-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <Tabs.Root defaultValue="files" className="flex flex-col h-[calc(85vh-80px)]">
            <Tabs.List className="flex gap-1 px-4 pt-2 border-b border-zinc-800">
              <Tabs.Trigger
                value="files"
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                  'data-[state=active]:bg-zinc-800 data-[state=active]:text-white',
                  'data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white'
                )}
              >
                <FolderOpen className="w-4 h-4 inline-block mr-2" />
                File Manager
              </Tabs.Trigger>
              <Tabs.Trigger
                value="display"
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                  'data-[state=active]:bg-zinc-800 data-[state=active]:text-white',
                  'data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white'
                )}
              >
                <Monitor className="w-4 h-4 inline-block mr-2" />
                Display
              </Tabs.Trigger>
              <Tabs.Trigger
                value="backup"
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                  'data-[state=active]:bg-zinc-800 data-[state=active]:text-white',
                  'data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white'
                )}
              >
                <Archive className="w-4 h-4 inline-block mr-2" />
                Backup
              </Tabs.Trigger>
              {isSignedIn && (
                <Tabs.Trigger
                  value="account"
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                    'data-[state=active]:bg-zinc-800 data-[state=active]:text-white',
                    'data-[state=inactive]:text-zinc-400 data-[state=inactive]:hover:text-white'
                  )}
                >
                  <User className="w-4 h-4 inline-block mr-2" />
                  Account
                </Tabs.Trigger>
              )}
            </Tabs.List>

            <div className="flex-1 overflow-auto p-4">
              <Tabs.Content value="files" className="h-full">
                <FileManager />
              </Tabs.Content>
              <Tabs.Content value="display" className="h-full">
                <DisplaySettings />
              </Tabs.Content>
              <Tabs.Content value="backup" className="h-full">
                <BackupSettings />
              </Tabs.Content>
              {isSignedIn && (
                <Tabs.Content value="account" className="h-full">
                  <AccountSettings />
                </Tabs.Content>
              )}
            </div>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface FileInfo {
  name: string
  size?: number
  path: string
}

function FileManager() {
  const { isConnected } = useConnection()
  const { listFiles, deleteFile, displayFile } = useSerialCommands()
  const { addToast } = useToast()
  const [files, setFiles] = useState<FileInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [deletingFile, setDeletingFile] = useState<string | null>(null)
  const [displayingFile, setDisplayingFile] = useState<string | null>(null)

  const fetchFiles = useCallback(async () => {
    if (!isConnected) return

    setIsLoading(true)
    try {
      const fileList = await listFiles()
      // Parse file info - format is "/filename (size bytes)"
      const parsed = fileList.map(f => {
        // Match: /filename (12345 bytes)
        const match = f.match(/^(\/[^\s]+)\s+\((\d+)\s+bytes\)$/)
        if (match) {
          return {
            name: match[1],
            size: parseInt(match[2], 10),
            path: match[1],
          }
        }
        // Fallback for files without size info
        return {
          name: f,
          size: undefined,
          path: f.startsWith('/') ? f : `/${f}`,
        }
      })
      setFiles(parsed)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to list files'
      addToast({
        type: 'error',
        title: 'Failed to list files',
        description: message,
      })
    } finally {
      setIsLoading(false)
    }
  }, [isConnected, listFiles, addToast])

  useEffect(() => {
    if (isConnected) {
      fetchFiles()
    }
  }, [isConnected, fetchFiles])

  const handleDelete = async (filename: string) => {
    if (!confirm(`Delete ${filename}?`)) return

    setDeletingFile(filename)
    try {
      await deleteFile(filename)
      addToast({
        type: 'success',
        title: 'File deleted',
        description: `${filename} has been deleted`,
      })
      fetchFiles()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete file'
      addToast({
        type: 'error',
        title: 'Failed to delete file',
        description: message,
      })
    } finally {
      setDeletingFile(null)
    }
  }

  const handleDisplay = async (filename: string) => {
    setDisplayingFile(filename)
    try {
      await displayFile(filename)
      addToast({
        type: 'success',
        title: 'Displaying image',
        description: `${filename} is being displayed`,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to display file'
      addToast({
        type: 'error',
        title: 'Failed to display file',
        description: message,
      })
    } finally {
      setDisplayingFile(null)
    }
  }

  const formatSize = (bytes?: number) => {
    if (bytes === undefined) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0)

  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
        <HardDrive className="w-12 h-12 mb-4 opacity-50" />
        <p>Connect to device to manage files</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          {files.length} files ({formatSize(totalSize)} total)
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={fetchFiles}
          disabled={isLoading}
        >
          <RefreshCw className={cn('w-4 h-4 mr-2', isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : files.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-zinc-500">
          <FolderOpen className="w-12 h-12 mb-4 opacity-50" />
          <p>No files on SD card</p>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-zinc-300">Filename</th>
                <th className="text-right px-4 py-2 font-medium text-zinc-300 w-24">Size</th>
                <th className="text-right px-4 py-2 font-medium text-zinc-300 w-20">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {files.map((file) => (
                <tr key={file.path} className="hover:bg-zinc-800/30">
                  <td className="px-4 py-2 text-zinc-200 font-mono text-xs">
                    {file.name}
                  </td>
                  <td className="px-4 py-2 text-right text-zinc-400">
                    {formatSize(file.size)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {file.name.endsWith('.bmp') && (
                        <button
                          onClick={() => handleDisplay(file.name)}
                          disabled={displayingFile === file.name}
                          className="text-blue-400 hover:text-blue-300 disabled:opacity-50 transition-colors"
                          title="Display on screen"
                        >
                          {displayingFile === file.name ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Monitor className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(file.name)}
                        disabled={deletingFile === file.name}
                        className="text-red-400 hover:text-red-300 disabled:opacity-50 transition-colors"
                        title="Delete file"
                      >
                        {deletingFile === file.name ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function DisplaySettings() {
  const { isConnected } = useConnection()
  const { getSetup, setSetup, getTime, syncTimeToBrowser } = useSerialCommands()
  const { addToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSyncingTime, setIsSyncingTime] = useState(false)
  const [deviceTime, setDeviceTime] = useState<DeviceTime | null>(null)
  const [settings, setSettings] = useState<ESPSetup>({
    auto_cycle: true,
    sleep_start_hour: 17,
    sleep_end_hour: 8,
    display_mode: 'calendar',
  })

  const fetchSettings = useCallback(async () => {
    if (!isConnected) return

    setIsLoading(true)
    try {
      const setup = await getSetup()
      setSettings({
        ...setup,
        auto_cycle: setup.auto_cycle ?? true,
        sleep_start_hour: setup.sleep_start_hour ?? 17,
        sleep_end_hour: setup.sleep_end_hour ?? 8,
        display_mode: setup.display_mode ?? 'calendar',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load settings'
      addToast({
        type: 'error',
        title: 'Failed to load settings',
        description: message,
      })
    } finally {
      setIsLoading(false)
    }
  }, [isConnected, getSetup, addToast])

  const fetchTime = useCallback(async () => {
    if (!isConnected) return

    try {
      const time = await getTime()
      setDeviceTime(time)
    } catch (error) {
      // Time fetch is optional, don't show error
      console.log('Could not fetch device time:', error)
    }
  }, [isConnected, getTime])

  useEffect(() => {
    if (isConnected) {
      fetchSettings()
      fetchTime()
    }
  }, [isConnected, fetchSettings, fetchTime])

  const handleSyncTime = async () => {
    setIsSyncingTime(true)
    try {
      await syncTimeToBrowser()
      addToast({
        type: 'success',
        title: 'Time synced',
        description: 'Device clock has been set to browser time',
      })
      // Refresh the displayed time
      await fetchTime()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sync time'
      addToast({
        type: 'error',
        title: 'Failed to sync time',
        description: message,
      })
    } finally {
      setIsSyncingTime(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await setSetup(settings)
      addToast({
        type: 'success',
        title: 'Settings saved',
        description: 'Display settings have been updated',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save settings'
      addToast({
        type: 'error',
        title: 'Failed to save settings',
        description: message,
      })
    } finally {
      setIsSaving(false)
    }
  }

  const formatHour = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:00 ${period}`
  }

  const getSleepDescription = () => {
    const start = settings.sleep_start_hour ?? 17
    const end = settings.sleep_end_hour ?? 8
    return `Updates paused from ${formatHour(start)} to ${formatHour(end)}`
  }

  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
        <Monitor className="w-12 h-12 mb-4 opacity-50" />
        <p>Connect to device to configure display settings</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Device Clock */}
      <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-zinc-400" />
          <h4 className="font-medium text-white">Device Clock</h4>
        </div>
        <p className="text-sm text-zinc-400">
          Sync the device's real-time clock with your browser for accurate photo scheduling.
        </p>

        {deviceTime && (
          <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-3 py-2">
            <div>
              <p className="text-sm text-zinc-300">Current device time</p>
              <p className="font-mono text-white">{deviceTime.timeString || 'Not set'}</p>
            </div>
            <div className={`text-xs px-2 py-1 rounded ${deviceTime.isSet ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
              {deviceTime.isSet ? 'RTC Set' : 'RTC Not Set'}
            </div>
          </div>
        )}

        <Button
          variant="secondary"
          onClick={handleSyncTime}
          disabled={isSyncingTime}
          className="w-full"
        >
          {isSyncingTime ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync to Browser Time
            </>
          )}
        </Button>
      </div>

      {/* Display Mode */}
      <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
        <div className="space-y-1">
          <h4 className="font-medium text-white">Display mode</h4>
          <p className="text-sm text-zinc-400">
            Choose how photos are selected for display
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setSettings(prev => ({ ...prev, display_mode: 'calendar' }))}
            className={cn(
              'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors',
              (settings.display_mode ?? 'calendar') === 'calendar'
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600'
            )}
          >
            <Calendar className="w-6 h-6 text-white" />
            <span className="text-sm font-medium text-white">Calendar</span>
            <span className="text-xs text-zinc-400 text-center">Show photos for today's date</span>
          </button>
          <button
            onClick={() => setSettings(prev => ({ ...prev, display_mode: 'shuffle' }))}
            className={cn(
              'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors',
              settings.display_mode === 'shuffle'
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600'
            )}
          >
            <Shuffle className="w-6 h-6 text-white" />
            <span className="text-sm font-medium text-white">Shuffle</span>
            <span className="text-xs text-zinc-400 text-center">Random from all photos</span>
          </button>
        </div>
      </div>

      {/* Auto Cycle Toggle */}
      <div className="bg-zinc-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h4 className="font-medium text-white">Auto cycle images</h4>
            <p className="text-sm text-zinc-400">
              Automatically rotate through images for each day on an hourly basis
            </p>
          </div>
          <Switch.Root
            checked={settings.auto_cycle ?? true}
            onCheckedChange={(checked: boolean) => setSettings(prev => ({ ...prev, auto_cycle: checked }))}
            className={cn(
              'w-11 h-6 rounded-full relative transition-colors',
              settings.auto_cycle ? 'bg-blue-500' : 'bg-zinc-600'
            )}
          >
            <Switch.Thumb className={cn(
              'block w-5 h-5 bg-white rounded-full transition-transform',
              settings.auto_cycle ? 'translate-x-[22px]' : 'translate-x-[2px]'
            )} />
          </Switch.Root>
        </div>
      </div>

      {/* Sleep Hours */}
      <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Moon className="w-5 h-5 text-zinc-400" />
          <h4 className="font-medium text-white">Sleep hours</h4>
        </div>
        <p className="text-sm text-zinc-400">
          During sleep hours, the display won't update to save power and avoid disturbance.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm text-zinc-300">Start (pause updates)</label>
            <select
              value={settings.sleep_start_hour ?? 17}
              onChange={(e) => setSettings(prev => ({ ...prev, sleep_start_hour: parseInt(e.target.value) }))}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{formatHour(i)}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-zinc-300">End (resume updates)</label>
            <select
              value={settings.sleep_end_hour ?? 8}
              onChange={(e) => setSettings(prev => ({ ...prev, sleep_end_hour: parseInt(e.target.value) }))}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{formatHour(i)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-blue-400 bg-blue-500/10 rounded-lg px-3 py-2">
          <Moon className="w-4 h-4" />
          {getSleepDescription()}
        </div>
      </div>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={isSaving}
        className="w-full"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            Save Settings
          </>
        )}
      </Button>
    </div>
  )
}

// Backup data structure
interface BackupData {
  version: number
  exportedAt: string
  setup: ESPSetup
  photoIndex: PhotoIndex
  photoMetadata: Record<string, { baseUrl: string; source: string }>
}

function BackupSettings() {
  const { isConnected } = useConnection()
  const { getSetup, setSetup, getIndex, setIndex, getMetadata, setMetadata } = useSerialCommands()
  const { addToast } = useToast()
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExport = async () => {
    if (!isConnected) return

    setIsExporting(true)
    try {
      // Fetch all data from device
      const [setup, photoIndex, photoMetadata] = await Promise.all([
        getSetup(),
        getIndex(),
        getMetadata(),
      ])

      const backupData: BackupData = {
        version: 1,
        exportedAt: new Date().toISOString(),
        setup,
        photoIndex,
        photoMetadata,
      }

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `calendar-backup-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      addToast({
        type: 'success',
        title: 'Backup exported',
        description: 'Your settings and photo index have been saved',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export backup'
      addToast({
        type: 'error',
        title: 'Export failed',
        description: message,
      })
    } finally {
      setIsExporting(false)
    }
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !isConnected) return

    setIsImporting(true)
    try {
      const text = await file.text()
      const backupData = JSON.parse(text) as BackupData

      // Validate backup format
      if (!backupData.version || !backupData.setup || !backupData.photoIndex) {
        throw new Error('Invalid backup file format')
      }

      // Restore data to device
      await setSetup(backupData.setup)
      await setIndex(backupData.photoIndex)
      if (backupData.photoMetadata) {
        await setMetadata(backupData.photoMetadata)
      }

      addToast({
        type: 'success',
        title: 'Backup restored',
        description: 'Your settings have been restored from backup',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to import backup'
      addToast({
        type: 'error',
        title: 'Import failed',
        description: message,
      })
    } finally {
      setIsImporting(false)
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
        <Archive className="w-12 h-12 mb-4 opacity-50" />
        <p>Connect to device to backup or restore settings</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Export Section */}
      <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Download className="w-5 h-5 text-zinc-400" />
          <h4 className="font-medium text-white">Export Backup</h4>
        </div>
        <p className="text-sm text-zinc-400">
          Download a backup file containing all your settings, photo index, and metadata.
          This file can be used to restore your configuration later.
        </p>
        <Button
          onClick={handleExport}
          disabled={isExporting}
          className="w-full"
        >
          {isExporting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Export Backup
            </>
          )}
        </Button>
      </div>

      {/* Import Section */}
      <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-zinc-400" />
          <h4 className="font-medium text-white">Restore from Backup</h4>
        </div>
        <p className="text-sm text-zinc-400">
          Restore your settings from a previously exported backup file.
          This will overwrite your current settings.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleImport}
        />
        <Button
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={isImporting}
          className="w-full"
        >
          {isImporting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Restoring...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Import Backup
            </>
          )}
        </Button>
      </div>

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-sm text-blue-300">
        <p className="font-medium mb-2">What's included in the backup:</p>
        <ul className="list-disc list-inside space-y-1 text-blue-400">
          <li>Display settings (auto-cycle, sleep hours)</li>
          <li>Photo index (which photos are assigned to which dates)</li>
          <li>Photo metadata (source information)</li>
        </ul>
        <p className="mt-2 text-zinc-400 text-xs">
          Note: The actual image files are not included in the backup.
        </p>
      </div>
    </div>
  )
}

function AccountSettings() {
  const { signOut } = useGoogleAuth()

  return (
    <div className="space-y-4">
      <div className="bg-zinc-800/50 rounded-lg p-4">
        <h4 className="font-medium text-white mb-4">Google Photos</h4>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-green-500">Connected</p>
            <p className="text-xs text-zinc-500 mt-1">
              You can import photos from Google Photos
            </p>
          </div>
          <Button variant="outline" onClick={signOut} className="text-red-400 border-red-400/50 hover:bg-red-400/10">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  )
}
